<template>
  <div class="policies-container">
    <!-- Header with Add button -->
    <b-row class="mb-3 align-items-center">
      <b-col cols="12" md="8">
        <h5 class="mb-0">{{ $t('policies.title') }}</h5>
        <small class="text-muted">{{ $t('policies.description') }}</small>
      </b-col>
      <b-col cols="12" md="4" class="text-right">
        <b-button variant="primary" @click="openCreateModal" size="sm">
          <b-icon icon="plus-circle" class="mr-1"></b-icon>
          {{ $t('policies.addPolicy') }}
        </b-button>
      </b-col>
    </b-row>

    <!-- Policies List -->
    <div v-if="policies.length === 0" class="text-center text-muted py-5">
      <b-icon icon="shield-lock" font-scale="3" class="mb-3"></b-icon>
      <p>{{ $t('policies.noPolicies') }}</p>
      <b-button variant="outline-primary" @click="openCreateModal" size="sm">
        <b-icon icon="plus-circle" class="mr-1"></b-icon>
        {{ $t('policies.createFirstPolicy') }}
      </b-button>
    </div>

    <b-card v-for="(policy, index) in policies" :key="index" class="mb-3 policy-card">
      <b-row class="align-items-center">
        <b-col cols="12" md="8">
          <div class="d-flex align-items-center mb-2">
            <b-icon 
              :icon="getPolicyIcon(policy.type)" 
              font-scale="1.5" 
              class="mr-2"
              :variant="policy.enabled ? 'success' : 'secondary'"
            ></b-icon>
            <div>
              <h6 class="mb-0">{{ policy.name }}</h6>
              <small class="text-muted">
                <b-badge :variant="getPolicyTypeBadgeVariant(policy.type)" class="mr-2">
                  {{ $t(`policies.types.${policy.type}`) }}
                </b-badge>
                <span v-if="policy.priority">{{ $t('policies.priority') }}: {{ policy.priority }}</span>
              </small>
            </div>
          </div>
          <p v-if="policy.description" class="mb-0 text-muted small">{{ policy.description }}</p>
          <div class="mt-2">
            <small class="text-muted">{{ getPolicySummary(policy) }}</small>
          </div>
        </b-col>
        <b-col cols="12" md="4" class="text-right">
          <b-form-checkbox 
            v-model="policy.enabled" 
            switch 
            size="lg"
            @change="onPolicyEnabledChange"
            class="d-inline-block mr-3"
          >
            {{ policy.enabled ? $t('policies.enabled') : $t('policies.disabled') }}
          </b-form-checkbox>
          <b-button variant="outline-primary" size="sm" @click="openEditModal(policy, index)" class="mr-1">
            <b-icon icon="pencil"></b-icon>
          </b-button>
          <b-button variant="outline-danger" size="sm" @click="deletePolicy(index)">
            <b-icon icon="trash"></b-icon>
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Create/Edit Modal -->
    <main-dialog 
      v-model="showModal" 
      :title="editingIndex !== null ? $t('policies.editPolicy') : $t('policies.createPolicy')"
      size="lg"
      @ok="savePolicy"
      @hidden="resetForm"
      :ok-disabled="!isFormValid"
    >
      <!-- Step 1: Basic Info -->
      <b-form-group :label="$t('policies.policyType')" label-for="policy-type">
        <b-form-select 
          id="policy-type" 
          v-model="form.type" 
          :options="policyTypeOptions"
          :disabled="editingIndex !== null"
        ></b-form-select>
      </b-form-group>

      <b-form-group :label="$t('policies.policyName')" label-for="policy-name">
        <b-form-input 
          id="policy-name" 
          v-model="form.name" 
          :placeholder="$t('policies.enterPolicyName')"
          required
        ></b-form-input>
      </b-form-group>

      <b-form-group :label="$t('policies.description')" label-for="policy-description">
        <b-form-textarea 
          id="policy-description" 
          v-model="form.description" 
          :placeholder="$t('policies.enterDescription')"
          rows="2"
        ></b-form-textarea>
      </b-form-group>

      <b-row>
        <b-col cols="6">
          <b-form-group :label="$t('policies.priority')" label-for="policy-priority">
            <b-form-input 
              id="policy-priority" 
              v-model.number="form.priority" 
              type="number"
              :placeholder="$t('policies.defaultPriority')"
            ></b-form-input>
            <small class="text-muted">{{ $t('policies.priorityHelp') }}</small>
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group :label="$t('policies.enabled')" label-for="policy-enabled">
            <b-form-checkbox 
              id="policy-enabled" 
              v-model="form.enabled" 
              switch
              size="lg"
            >
              {{ form.enabled ? $t('policies.enabled') : $t('policies.disabled') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <hr class="my-4">

      <!-- Step 2: Type-Specific Config -->
      <h6 class="mb-3">{{ $t('policies.configuration') }}</h6>

      <!-- Field Access Config -->
      <div v-if="form.type === 'field_access'">
        <b-form-group :label="$t('policies.selectFields')" label-for="config-fields">
          <v-select 
            id="config-fields"
            v-model="form.config.fields" 
            :options="availableFields"
            label="label"
            :reduce="field => field.value"
            multiple
            :placeholder="$t('policies.selectFieldsPlaceholder')"
          ></v-select>
        </b-form-group>

        <b-form-group :label="$t('policies.selectRoles')" label-for="config-roles">
          <v-select 
            id="config-roles"
            v-model="form.config.roles" 
            :options="availableRoles"
            label="name"
            :reduce="role => role.uuid"
            multiple
            :placeholder="$t('policies.selectRolesPlaceholder')"
          ></v-select>
        </b-form-group>

        <b-form-group :label="$t('policies.allowedActions')" label-for="config-actions">
          <div class="action-checkboxes">
            <b-form-checkbox-group v-model="form.config.actions" stacked>
              <b-form-checkbox value="read">{{ $t('policies.actions.read') }}</b-form-checkbox>
              <b-form-checkbox value="update">{{ $t('policies.actions.update') }}</b-form-checkbox>
            </b-form-checkbox-group>
          </div>
        </b-form-group>
      </div>

      <!-- Data Access Config -->
      <div v-if="form.type === 'data_access'">
        <b-form-group :label="$t('policies.selectRoles')" label-for="config-roles-data">
          <v-select 
            id="config-roles-data"
            v-model="form.config.roles" 
            :options="availableRoles"
            label="name"
            :reduce="role => role.uuid"
            multiple
            :placeholder="$t('policies.selectRolesPlaceholder')"
          ></v-select>
        </b-form-group>

        <b-form-group :label="$t('policies.filterConditions')" label-for="config-filter">
          <b-alert show variant="info" class="mb-2">
            <small>
              <b-icon icon="info-circle" class="mr-1"></b-icon>
              {{ $t('policies.templateVariablesHelp') }}
              <br>
              <code v-text="'{{user.id}}'"></code>, <code v-text="'{{user.email}}'"></code>, <code v-text="'{{user.region}}'"></code>, <code v-text="'{{user.department}}'"></code>
            </small>
          </b-alert>
          <CodeEditor v-model="filterJson" :withButton="false" :height="200" />
          <small class="text-muted">{{ $t('policies.filterExample') }}: <code v-text='`{"owner_id": "{{user.id}}"}`'></code></small>
        </b-form-group>

        <b-form-group :label="$t('policies.allowedActions')" label-for="config-actions-data">
          <div class="action-checkboxes">
            <b-form-checkbox-group v-model="form.config.actions" stacked>
              <b-form-checkbox value="read">{{ $t('policies.actions.read') }}</b-form-checkbox>
              <b-form-checkbox value="create">{{ $t('policies.actions.create') }}</b-form-checkbox>
              <b-form-checkbox value="update">{{ $t('policies.actions.update') }}</b-form-checkbox>
              <b-form-checkbox value="delete">{{ $t('policies.actions.delete') }}</b-form-checkbox>
            </b-form-checkbox-group>
          </div>
        </b-form-group>
      </div>

      <!-- Limits Config -->
      <div v-if="form.type === 'limits'">
        <b-row>
          <b-col cols="6">
            <b-form-group :label="$t('policies.requestsLimit')" label-for="config-requests">
              <b-form-input 
                id="config-requests" 
                v-model.number="form.config.requests" 
                type="number"
                :placeholder="$t('policies.enterRequestsLimit')"
                required
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group :label="$t('policies.timeWindow')" label-for="config-window">
              <b-form-input 
                id="config-window" 
                v-model.number="form.config.window" 
                type="number"
                :placeholder="$t('policies.enterTimeWindow')"
                required
              ></b-form-input>
              <small class="text-muted">{{ $t('policies.timeWindowHelp') }}</small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group :label="$t('policies.allowedActions')" label-for="config-actions-limits">
          <div class="action-checkboxes">
            <b-form-checkbox-group v-model="form.config.actions" stacked>
              <b-form-checkbox value="read">{{ $t('policies.actions.read') }}</b-form-checkbox>
              <b-form-checkbox value="create">{{ $t('policies.actions.create') }}</b-form-checkbox>
              <b-form-checkbox value="update">{{ $t('policies.actions.update') }}</b-form-checkbox>
              <b-form-checkbox value="delete">{{ $t('policies.actions.delete') }}</b-form-checkbox>
            </b-form-checkbox-group>
          </div>
        </b-form-group>

        <b-form-group :label="$t('policies.selectRoles')" label-for="config-roles-limits">
          <v-select 
            id="config-roles-limits"
            v-model="form.config.roles" 
            :options="availableRoles"
            label="name"
            :reduce="role => role.uuid"
            multiple
            :placeholder="$t('policies.selectRolesPlaceholder')"
          ></v-select>
        </b-form-group>

        <b-form-group :label="$t('policies.strategy')" label-for="config-strategy">
          <b-form-select 
            id="config-strategy" 
            v-model="form.config.strategy" 
            :options="strategyOptions"
          ></b-form-select>
          <small class="text-muted">{{ $t('policies.strategyHelp') }}</small>
        </b-form-group>
      </div>
    </main-dialog>
  </div>
</template>

<script>
import VSelect from 'vue-select'
import CodeEditor from '@/components/form/Editor/Code'

export default {
  name: 'Policies',
  components: {
    VSelect,
    CodeEditor
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    component: {
      type: Object,
      required: true
    },
    componentRef: {
      type: String,
      required: true
    },
    componentVersion: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      policies: [],
      showModal: false,
      editingIndex: null,
      form: {
        type: 'field_access',
        name: '',
        description: '',
        enabled: true,
        priority: 0,
        config: {
          fields: [],
          roles: [],
          actions: [],
          filter: {},
          requests: 100,
          window: 60,
          endpoints: [],
          strategy: 'sliding'
        }
      },
      filterJson: '{}'
    }
  },
  computed: {
    policyTypeOptions() {
      return [
        { value: 'field_access', text: this.$t('policies.types.field_access') }
        // { value: 'data_access', text: this.$t('policies.types.data_access') },
        // { value: 'limits', text: this.$t('policies.types.limits') }
      ]
    },
    strategyOptions() {
      return [
        { value: 'sliding', text: this.$t('policies.strategyTypes.sliding') },
        { value: 'fixed', text: this.$t('policies.strategyTypes.fixed') }
      ]
    },
    availableFields() {
      if (!this.component || !this.component.settings) return []
      
      // Get regular fields
      const regularFields = (this.component.settings.fields || [])
        .filter(f => f.key)
        .map(f => ({
          value: f.key,
          label: f.key
        }))
      
      // Get extension fields
      const extensionFields = []
      const extensions = this.component.settings.extensions || []
      extensions.forEach(extension => {
        if (extension.is_active && extension.fields) {
          extension.fields.forEach(field => {
            if (field.key) {
              extensionFields.push({
                value: `${extension.name}.${field.key}`,
                label: `${field.key} (${extension.name})`
              })
            }
          })
        }
      })
      
      return [...regularFields, ...extensionFields]
    },
    availableRoles() {
      const roles = this.$store.getters.getRoles || []
      return roles.map(role => ({
        uuid: role.uuid,
        name: role.name
      }))
    },
    isFormValid() {
      if (!this.form.name || !this.form.type) return false
      
      const config = this.form.config
      
      switch (this.form.type) {
        case 'field_access':
          return (config.fields && config.fields.length > 0) && 
                 (config.roles && config.roles.length > 0) && 
                 (config.actions && config.actions.length > 0)
        case 'data_access':
          return (config.roles && config.roles.length > 0) && 
                 (config.actions && config.actions.length > 0) &&
                 this.isValidJson(this.filterJson)
        case 'limits':
          return config.requests > 0 && 
                 config.window > 0 && 
                 (config.actions && config.actions.length > 0) && 
                 (config.roles && config.roles.length > 0)
        default:
          return false
      }
    }
  },
  watch: {
    value: {
      handler(val) {
        this.policies = val || []
      },
      immediate: true,
      deep: true
    },
    policies: {
      handler(val) {
        this.$emit('input', val)
      },
      deep: true
    },
    'form.type'() {
      // Reset config when type changes
      this.form.config = {
        fields: [],
        roles: [],
        actions: [],
        filter: {},
        requests: 100,
        window: 60,
        strategy: 'sliding'
      }
      this.filterJson = '{}'
    }
  },
  methods: {
    openCreateModal() {
      this.editingIndex = null
      this.resetForm()
      this.showModal = true
    },
    openEditModal(policy, index) {
      this.editingIndex = index
      this.form = JSON.parse(JSON.stringify(policy))
      
      // Convert filter object to JSON string for data_access
      if (policy.type === 'data_access' && policy.config.filter) {
        this.filterJson = JSON.stringify(policy.config.filter, null, 2)
      }
      
      this.showModal = true
    },
    savePolicy() {
      // Parse filter JSON for data_access
      if (this.form.type === 'data_access') {
        try {
          this.form.config.filter = JSON.parse(this.filterJson)
        } catch (e) {
          this.$bvToast.toast(this.$t('policies.invalidJsonFilter'), {
            title: this.$t('error'),
            variant: 'danger'
          })
          return
        }
      }
      
      // Clean up config based on type
      const cleanedForm = JSON.parse(JSON.stringify(this.form))
      if (cleanedForm.type === 'field_access') {
        delete cleanedForm.config.filter
        delete cleanedForm.config.requests
        delete cleanedForm.config.window
        delete cleanedForm.config.endpoints
        delete cleanedForm.config.strategy
      } else if (cleanedForm.type === 'data_access') {
        delete cleanedForm.config.fields
        delete cleanedForm.config.requests
        delete cleanedForm.config.window
        delete cleanedForm.config.strategy
      } else if (cleanedForm.type === 'limits') {
        delete cleanedForm.config.fields
        delete cleanedForm.config.filter
      }
      
      if (this.editingIndex !== null) {
        this.updatePolicyAPI(cleanedForm.name, cleanedForm)
      } else {
        this.createPolicyAPI(cleanedForm)
      }
      
      this.showModal = false
    },
    async createPolicyAPI(policy) {
      const popup = this.$loadingPopup()
      
      try {
        const response = await this.$http.post(
          `/v4/system/component/${this.componentRef}/${this.componentVersion}/policies`, 
          policy
        )
        
        // Use the returned settings to update policies
        if (response.data && response.data.policies) {
          this.$emit('input', response.data.policies)
        }
        
        this.$bvToast.toast(this.$t('policies.policyCreated'), {
          title: this.$t('success'),
          variant: 'success'
        })
      } catch (error) {
        console.error('Error creating policy:', error)
        const message = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : this.$t('policies.failedToCreatePolicy')
        
        this.$bvToast.toast(message, {
          title: this.$t('error'),
          variant: 'danger'
        })
      } finally {
        popup.hide()
      }
    },

    async updatePolicyAPI(name, policy) {
      const popup = this.$loadingPopup()
      
      try {
        const response = await this.$http.patch(
          `/v4/system/component/${this.componentRef}/${this.componentVersion}/policies/${name}`, 
          policy
        )
        
        // Use the returned settings to update policies
        if (response.data && response.data.policies) {
          this.$emit('input', response.data.policies)
        }
        
        this.$bvToast.toast(this.$t('policies.policyUpdated'), {
          title: this.$t('success'),
          variant: 'success'
        })
      } catch (error) {
        console.error('Error updating policy:', error)
        const message = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : this.$t('policies.failedToUpdatePolicy')
        
        this.$bvToast.toast(message, {
          title: this.$t('error'),
          variant: 'danger'
        })
      } finally {
        popup.hide()
      }
    },

    confirmDeletePolicy(index) {
      const policy = this.policies[index]
      this.$mainDialog.msgBoxConfirm(
        this.$t('policies.confirmDelete', { name: policy.name }),
        {
          title: this.$t('confirm'),
          okVariant: 'danger',
          okTitle: this.$t('delete'),
          cancelTitle: this.$t('cancel')
        }
      ).then(confirmed => {
        if (confirmed) {
          this.deletePolicyAPI(index)
        }
      })
    },

    async deletePolicyAPI(index) {
      const policy = this.policies[index]
      const popup = this.$loadingPopup()

      try {
        const response = await this.$http.delete(
          `/v4/system/component/${this.componentRef}/${this.componentVersion}/policies/${policy.name}`
        )

        // Use the returned settings to update policies
        if (response.data && response.data.settings && response.data.settings.policies) {
          this.$emit('input', response.data.settings.policies)
        } else if (response.data && response.data.policies) {
          this.$emit('input', response.data.policies)
        }

        this.$bvToast.toast(this.$t('policies.policyDeleted', { name: policy.name }), {
          title: this.$t('success'),
          variant: 'success'
        })
      } catch (error) {
        console.error('Error deleting policy:', error)
        const message = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : this.$t('policies.failedToDeletePolicy')
        
        this.$bvToast.toast(message, {
          title: this.$t('error'),
          variant: 'danger'
        })
      } finally {
        popup.hide()
      }
    },

    deletePolicy(index) {
      this.confirmDeletePolicy(index)
    },
    resetForm() {
      this.form = {
        type: 'field_access',
        name: '',
        description: '',
        enabled: true,
        priority: 0,
        config: {
          fields: [],
          roles: [],
          actions: [],
          filter: {},
          requests: 100,
          window: 60,
          strategy: 'sliding'
        }
      }
      this.filterJson = '{}'
    },
    onPolicyEnabledChange() {
      this.$bvToast.toast(this.$t('policies.policyStatusChanged'), {
        title: this.$t('success'),
        variant: 'info'
      })
    },
    getPolicyIcon(type) {
      const icons = {
        field_access: 'shield-lock',
        data_access: 'filter',
        limits: 'speedometer2'
      }
      return icons[type] || 'shield'
    },
    getPolicyTypeBadgeVariant(type) {
      const variants = {
        field_access: 'primary',
        data_access: 'success',
        limits: 'warning'
      }
      return variants[type] || 'secondary'
    },
    getPolicySummary(policy) {
      const config = policy.config
      
      switch (policy.type) {
        case 'field_access':
          return `${(config.fields && config.fields.length) || 0} ${this.$t('policies.fields')} • ${(config.roles && config.roles.length) || 0} ${this.$t('policies.roles')} • ${(config.actions && config.actions.length) || 0} ${this.$t('policies.actionsCount')}`
        case 'data_access':
          return `${(config.roles && config.roles.length) || 0} ${this.$t('policies.roles')} • ${(config.actions && config.actions.length) || 0} ${this.$t('policies.actionsCount')} • ${this.$t('policies.filtered')}`
        case 'limits':
          return `${config.requests} ${this.$t('policies.requests')} / ${config.window}s • ${(config.roles && config.roles.length) || 0} ${this.$t('policies.roles')} • ${(config.actions && config.actions.length) || 0} ${this.$t('policies.actionsCount')}`
        default:
          return ''
      }
    },
    isValidJson(str) {
      try {
        JSON.parse(str)
        return true
      } catch (e) {
        return false
      }
    }
  },
  mounted() {
    this.$store.dispatch('fetchRoles')
  }
}
</script>

<style scoped>
.policies-container {
  padding: 1rem;
}

.policy-card {
  transition: box-shadow 0.2s;
}

.policy-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.action-checkboxes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
}

::v-deep .vs__dropdown-toggle {
  background-color: var(--card-bg, #fff);
  border-color: var(--border-color, #d8dbe0);
}

::v-deep .vs__selected {
  color: var(--text-color, #23282c);
}
</style>
